import { normalize } from 'normalizr';

export type ApiAction = {
  // Three redux action type names: request, success, error
  actions: (string | string[])[];
  // Optional normalizr
  normalizer?: any,
  // Function to call, function should return a promise
  fn: (state?: any) => any;
  // Do additional processing after successful request and applied normalizer (if any)
  then?: (response) => any;
  // Filter function, optional. If false is returned - API call won't be made
  shouldCall?: (store) => boolean;
  // Optional arguments to inject into actions
  payload?: {};
}

/**
 * apiCall() wraps redux dispatcher and getState to reduce boilerplate code
 * when calling various APIs. It is expected that `actions` argument contains
 * exactly three action names (request, success, error) and conditionally
 * dispatches them upon success or failure.
 */
export function apiCall(action: ApiAction) {
  return (dispatch, getState) => {
    const {
      actions,
      fn,
      shouldCall = () => true,
      normalizer: normalizeRule,
      then = (response) => response,
      payload = {}
    } = action;

    if (actions.length !== 3)
      throw new Error('Expected an array of three string types.');

    const state = getState();

    if (!shouldCall(state)) {
      return
    }

    const [requestType, successType, failureType] = actions;

    dispatch({
      ...payload,
      type: requestType,
    });

    return fn(state)
      .then((response: any) => {
        // TODO: Figure out if empty or undefined response is a valid response
        if (response === null) {
          throw new Error('A null response from the server.');
        }

        const data = normalizeRule ? normalize(response, normalizeRule) : response;
        return then(data);
      })
      .then((response) => {
        // successType can be an array of events
        if (Array.isArray(successType)) {
          for (let ntype of successType) {
            dispatch({
              ...payload,
              type: ntype,
              ...response,
            })
          }
        } else {
          dispatch({
            ...payload,
            type: successType,
            ...response,
          })
        }

        return response;
      })
      .catch((error) => {
        console.error('API call failure', error);
        dispatch({
          ...payload,
          type: failureType,
          error,
        });

        return Promise.reject(error);
      });
  };
}
